import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import TenantSwitcher from './TenantSwitcher';
import { getRolePermissions } from '../../utils/rolePermissions';

const Sidebar = ({ isOpen, onClose }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('saleswork_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { crmSettings } = useSettings();
  const { logout, currentUser } = useAuth();
  const { activeMembership, activeTenant } = useTenant();
  const tenantName = activeTenant?.name || 'Sales Work';
  const tenantPlan = activeTenant?.plan || 'CRM';

  const effectivePermissions = getRolePermissions(currentUser, activeMembership, activeTenant);

  const handleSidebarToggle = () => {
    const next = !isSidebarCollapsed;
    setIsSidebarCollapsed(next);

    try {
      localStorage.setItem('saleswork_sidebar_collapsed', String(next));
    } catch {
      // Keep the selected state in memory if storage is unavailable.
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper to check specific role visibility
  const isRole = (roles) => roles.includes(currentUser?.role);

  const navigation = [
    { name: 'Oversikt', href: '/dashboard', icon: '📊', allowed: true },
    { name: 'Compliance Funnel', href: '/dashboard/compliance', icon: '🛡️', allowed: effectivePermissions.canSettings },
    { name: 'Compliance (Styre)', href: '/dashboard/board-compliance', icon: '👁️', allowed: effectivePermissions.canSettings },
    { name: 'Rapportering', href: '/reports', icon: '📈', allowed: effectivePermissions.canViewReports },
    { name: 'Mine Salg', href: '/seller', icon: '👤', allowed: effectivePermissions.canViewCRM },
    { name: 'Scoreboard', href: '/scoreboard', icon: '🏆', allowed: effectivePermissions.canViewCRM && crmSettings?.enableScoreboard },
    { name: 'Pipeline', href: '/pipeline', icon: '💰', allowed: effectivePermissions.canViewPipeline },
    { name: 'Forecast', href: '/forecast', icon: '📉', allowed: effectivePermissions.canViewCRM },
    { name: 'Prosjekter', href: '/projects', icon: '🏗️', allowed: effectivePermissions.canViewCRM },
    
    { section: 'Salg & Kontrakter' },
    { name: 'Kontraktmaler', href: '/contract-templates', icon: '📄', allowed: effectivePermissions.canSettings },
    { name: 'E-Signering', href: '/contracts', icon: '✍️', allowed: effectivePermissions.canViewCRM },
    { name: 'Produkter', href: '/products', icon: '📦', allowed: effectivePermissions.canSettings },

    { section: 'Automasjon & Leads' },
    { name: 'Lead Intelligence', href: '/lead-intelligence', icon: '🧠', highlight: true, allowed: effectivePermissions.canOutreach },
    { name: 'Lead Import (BRREG)', href: '/lead-import', icon: '🏢', allowed: effectivePermissions.canImportBrreg },
    { name: 'LinkedIn Outreach', href: '/outreach', icon: '👔', allowed: effectivePermissions.canOutreach },
    { name: 'Email Copilot', href: '/email-copilot', icon: '🤖', allowed: effectivePermissions.canOutreach },
    { name: 'AI Coach', href: '/ai-coach', icon: '⚡', allowed: effectivePermissions.canOutreach },
    { name: 'Playbook Store', href: '/marketplace', icon: '🛒', allowed: effectivePermissions.canSettings },
    { name: 'Sekvenser', href: '/sequences', icon: '🔄', allowed: effectivePermissions.canOutreach },
    { name: 'Import Sekvenser', href: '/sequences/import', icon: '📥', allowed: effectivePermissions.canOutreach },
    { name: 'Import Maler', href: '/email-templates/import', icon: '📥', allowed: effectivePermissions.canOutreach },

    { section: 'Kommunikasjon' },
    { name: 'Kalender', href: '/activities?view=calendar', icon: '📅', allowed: effectivePermissions.canViewCRM },
    { name: 'Masse-epost', href: '/mass-email', icon: '📧', allowed: effectivePermissions.canOutreach },

    { section: 'Kunderelasjoner' },
    { name: 'Oppdragsgivere', href: '/clients', icon: '🤝', allowed: effectivePermissions.canViewCRM },
    { name: 'Kunder', href: '/customers', icon: '⭐', allowed: effectivePermissions.canViewCRM },
    { name: 'Selskaper', href: '/companies', icon: '🏢', allowed: effectivePermissions.canViewCRM },
    { name: 'Kontakter', href: '/contacts', icon: '👥', allowed: effectivePermissions.canViewCRM },
    { name: 'Kunderom', href: '/customer-rooms', icon: '🔒', allowed: effectivePermissions.canViewCRM },
    { name: 'Aktiviteter', href: '/activities', icon: '✅', allowed: effectivePermissions.canViewCRM },

    { section: 'Markedsføring' },
    { name: 'Sosiale medier', href: '/social', icon: '🌐', allowed: effectivePermissions.canOutreach },

    { section: 'System' },
    { name: 'Governance (ISO)', href: '/governance', icon: '⚖️', allowed: effectivePermissions.canSettings },
    { name: 'Kunnskapsbase', href: '/knowledge-base', icon: '📚', allowed: effectivePermissions.canViewKnowledgeBase },
    { name: 'E-postmaler', href: '/email-templates', icon: '📨', allowed: effectivePermissions.canSettings },
    { name: 'Integrasjoner', href: '/integrations', icon: '🔌', allowed: effectivePermissions.canSettings },
    { name: 'Datakilder (BRREG)', href: '/settings/brreg', icon: '🏢', allowed: effectivePermissions.canImportBrreg },
    { name: 'Innstillinger', href: '/settings', icon: '⚙️', allowed: effectivePermissions.canSettings },
  ];

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity" onClick={onClose} />
        </div>
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-50 ${isSidebarCollapsed ? 'w-16' : 'w-72'} shrink-0 bg-sidebar-bg text-slate-100 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col border-r border-white/5 shadow-xl
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-between px-6'} h-20 mb-2`}>
          <div className={`flex items-center ${isSidebarCollapsed ? '' : 'gap-3'}`}>
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-glow font-bold text-lg text-white">
              S
            </div>
            {!isSidebarCollapsed && (
              <div>
                <h1 className="text-base font-display font-bold text-white tracking-tight leading-tight">{tenantName}</h1>
                <p className="text-[9px] text-blue-200 font-medium uppercase tracking-wider">{tenantPlan}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSidebarToggle}
              aria-label={isSidebarCollapsed ? 'Utvid meny' : 'Minimer meny'}
              title={isSidebarCollapsed ? 'Utvid meny' : 'Minimer meny'}
              className="hidden lg:flex p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <SafeIcon icon={isSidebarCollapsed ? '›' : '‹'} />
            </button>
            <button onClick={onClose} className="lg:hidden p-1 rounded-md text-slate-400 hover:text-white transition-colors" aria-label="Lukk meny">
              ✕
            </button>
          </div>
        </div>

        {/* Tenant Switcher */}
        {effectivePermissions.canSwitchTenant && !isSidebarCollapsed && <TenantSwitcher />}

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-2 ${isSidebarCollapsed ? 'px-2' : 'px-4'} space-y-1 custom-scrollbar`}>
          {navigation.map((item, index) => {
            if (!item.allowed && !item.section) return null;

            if (item.section) {
              // Only show if children items are allowed
              let hasAllowedItems = false;
              for(let i = index + 1; i < navigation.length; i++) {
                if(navigation[i].section) break;
                if(navigation[i].allowed) {
                  hasAllowedItems = true;
                  break;
                }
              }
              if(!hasAllowedItems || isSidebarCollapsed) return null;

              return (
                <div key={index} className="pt-6 pb-2 px-3">
                  <p className="text-xs font-bold text-blue-200/60 uppercase tracking-widest">{item.section}</p>
                </div>
              );
            }

            const isCalendarView = location.pathname === '/activities'
              && new URLSearchParams(location.search).get('view') === 'calendar';
            const isActivitiesView = location.pathname === '/activities'
              && new URLSearchParams(location.search).get('view') !== 'calendar';
            const isActive = item.href === '/activities?view=calendar'
              ? isCalendarView
              : item.href === '/activities'
                ? isActivitiesView
                : location.pathname === item.href;

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                title={isSidebarCollapsed ? item.name : undefined}
                className={`
                  group flex items-center ${isSidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-primary-600 text-white shadow-glow border border-primary-500/50' 
                    : 'text-slate-300 hover:bg-white/10 hover:text-white border border-transparent'}
                  ${item.highlight ? 'ring-1 ring-emerald-500/50 bg-emerald-500/10' : ''}
                `}
              >
                <span className={`${isSidebarCollapsed ? '' : 'mr-3'} text-lg transition-transform group-hover:scale-110 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}>
                  <SafeIcon icon={item.icon} />
                </span>
                {!isSidebarCollapsed && item.name}
                {isActive && !isSidebarCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                )}
                {item.highlight && !isActive && !isSidebarCollapsed && (
                  <span className="ml-auto text-[8px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">Ny</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={`${isSidebarCollapsed ? 'p-2' : 'p-4'} border-t border-white/10 bg-black/20`}>
          <button
            onClick={handleLogout}
            title={isSidebarCollapsed ? 'Logg ut' : undefined}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center p-2' : 'w-full p-3'} rounded-xl hover:bg-white/10 transition-colors group border border-transparent hover:border-white/5`}
          >
            <div className="w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center text-slate-200 group-hover:text-white text-xs border border-white/10">
              🚪
            </div>
            {!isSidebarCollapsed && (
              <div className="ml-3 text-left">
                <p className="text-sm font-bold text-slate-200 group-hover:text-white">Logg ut</p>
                <p className="text-[10px] text-slate-400 group-hover:text-slate-300">Avslutt økten</p>
              </div>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;